import { getFirstSelection } from '../../selectionUtils/tableSelectionAccessors';
import { isSelectingSingleSelection } from '../../selectionUtils/tableSelectionPredicateUtils';
import type { MNHotInstance } from '../../../tableHotInstanceTypes';
import { isFormula } from '../../dataUtils/formula/tableFormulaPredicateUtils';
import { FORMULA_AUTOCOMPLETE_PLUGIN_NAME } from '../../../plugins/FormulaAutocompleteHotPlugin';
import type FormulaAutocompleteHotPlugin from '../../../plugins/FormulaAutocompleteHotPlugin';
import type DraftJSFormulaAutocompleteHotPlugin from '../../../plugins/DraftJSFormulaAutocompleteHotPlugin';
import { DRAFTJS_FORMULA_AUTOCOMPLETE_PLUGIN_NAME } from '../../../plugins/DraftJSFormulaAutocompleteHotPlugin';
import type { CellSelection } from '../../../../../../common/table/TableTypes';
import { MilanoteApplicationMode } from '../../../../../../common/platform/platformTypes';

const FORMULA_TRIGGER = '=';

type TableFormulaEditor = NonNullable<ReturnType<MNHotInstance['getMNEditor']>>;

type FormulaEditingResult = {
    cellContent: string;
    shouldOpenFormulaPicker: boolean;
};

const isEnabledFormulaAutocompletePlugin = (
    plugin: DraftJSFormulaAutocompleteHotPlugin | FormulaAutocompleteHotPlugin | undefined,
): boolean => !!plugin && (!plugin.isEnabled || plugin.isEnabled());

const isMobileMode = (hotInstance: MNHotInstance): boolean =>
    hotInstance.milanoteProps?.appMode === MilanoteApplicationMode.mobile;

// TODO: This can be removed and inlined once DraftJSFormulaAutocompleteHotPlugin is removed
const getFormulaAutocompletePlugin = (
    hotInstance: MNHotInstance,
): DraftJSFormulaAutocompleteHotPlugin | FormulaAutocompleteHotPlugin | undefined => {
    const plugins = [
        hotInstance.getPlugin(FORMULA_AUTOCOMPLETE_PLUGIN_NAME),
        hotInstance.getPlugin(DRAFTJS_FORMULA_AUTOCOMPLETE_PLUGIN_NAME),
    ] as unknown as Array<DraftJSFormulaAutocompleteHotPlugin | FormulaAutocompleteHotPlugin | undefined>;

    return plugins.find(isEnabledFormulaAutocompletePlugin);
};

/**
 * Starts formula editing from a selected cell.
 */
const beginFormulaEditing = (
    hotInstance: MNHotInstance,
    activeEditor: TableFormulaEditor,
    cellSelection: CellSelection,
): FormulaEditingResult => {
    hotInstance.selectCell(...cellSelection, false, true);

    const [row, col] = cellSelection;
    const cellContent = hotInstance.getSourceDataAtCell(row, col)?.toString() || '';

    // If cell is already a formula, preserve the content of the cell and start editing
    if (isFormula(cellContent)) {
        activeEditor.beginEditing();
        return { cellContent, shouldOpenFormulaPicker: isMobileMode(hotInstance) };
    }
    // If cell is not a formula, begin editing cell with `=`
    activeEditor.beginEditing(FORMULA_TRIGGER);
    return { cellContent: FORMULA_TRIGGER, shouldOpenFormulaPicker: true };
};

/**
 * Prepares and focuses an already open editor for formula autocomplete.
 */
const continueEditingForFormula = (activeEditor: TableFormulaEditor): FormulaEditingResult => {
    const cellContent = activeEditor.getPlainTextContent();

    // If cell is already a formula, preserve the content and open the picker so
    // another formula can be added to the existing equation at the caret
    if (isFormula(cellContent)) {
        activeEditor.focus();
        return { cellContent, shouldOpenFormulaPicker: true };
    }

    // If cell is not a formula, replace cell content with `=`
    activeEditor.setPlainTextContent(FORMULA_TRIGGER);
    activeEditor.focus();
    return { cellContent: FORMULA_TRIGGER, shouldOpenFormulaPicker: true };
};

/**
 * Adds a formula to the currently selected cell in the Handsontable instance.
 */
export const addHotTableFormulaToCell = (hotInstance: MNHotInstance, cellSelections = hotInstance.getSelected()) => {
    if (!cellSelections) return;

    if (!isSelectingSingleSelection(cellSelections)) return;

    const cellSelection = getFirstSelection(cellSelections);
    if (!cellSelection) return;

    requestAnimationFrame(() => {
        if (hotInstance.isDestroyed) return;

        const activeEditor = hotInstance.getMNEditor();
        if (!activeEditor) return;

        const { cellContent, shouldOpenFormulaPicker } = activeEditor.isOpened()
            ? continueEditingForFormula(activeEditor)
            : beginFormulaEditing(hotInstance, activeEditor, cellSelection);

        if (shouldOpenFormulaPicker) getFormulaAutocompletePlugin(hotInstance)?.openFormulaPicker(cellContent);
    });
};
